import styled from "@emotion/styled";
import {
  type CoworkerAndTeamSelectionMode,
  type SelectCoworkerAndTeamModalDefinition,
  type TeamSelection,
  isTeamSelection,
} from "@repo/api";
import { Badge, Pill, Variables, chevronDownIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { getUserInfoFromEmail, hasItems } from "@repo/utils";

import { useCoWorkerName } from "../CoWorkerName";
import { VisuallyHidden } from "../VisuallyHidden";
import { type SelectCoworkerAndTeamModalProps } from "./SelectCoworkerAndTeamModal";
import { getSelectionModeText } from "./utils";

interface Props {
  selectionMode: CoworkerAndTeamSelectionMode;
  selected: TeamSelection | string[] | undefined;
  setSelected: SelectCoworkerAndTeamModalProps["setSelected"];
  pushModal: (modalProps: SelectCoworkerAndTeamModalDefinition) => void;
  searchContext?: "PROJECT" | "CALENDAR";
}

const TeamIdBadge = styled(Badge)`
  margin-left: ${Variables.space25};
`;

export const CoworkerAndTeamSelectorPill = ({
  selectionMode,
  selected,
  setSelected,
  pushModal,
  searchContext,
}: Props) => {
  const emailIds = Array.isArray(selected) ? selected : undefined;
  const singleEmail = emailIds?.length === 1 ? emailIds[0] : undefined;
  const { name: coworkerName } = useCoWorkerName(singleEmail);

  const getLabel = () => {
    if (hasItems(emailIds)) {
      if (emailIds.length === 1) {
        return coworkerName ?? emailIds[0];
      }
      return t("prompt.coworkers_selected", { number: emailIds.length });
    } else if (isTeamSelection(selected)) {
      return selected.team.teamName;
    } else {
      return getSelectionModeText(selectionMode)?.title;
    }
  };

  return (
    <Pill
      size="small"
      label={
        <>
          <>{getLabel()}</>
          {isTeamSelection(selected) && (
            <TeamIdBadge
              colour="grey"
              label={`${t("label.ID")}: ${selected.team.teamId}`}
              size="small"
            />
          )}
          <VisuallyHidden>{t("label.select_coworker_or_team")}</VisuallyHidden>
        </>
      }
      trailingIcon={chevronDownIcon}
      onClick={() =>
        pushModal({
          type: "SELECT_TEAM_OR_COWORKER",
          selectionMode,
          selected: Array.isArray(selected)
            ? selected.map((email) => getUserInfoFromEmail(email))
            : selected,
          setSelected,
          searchContext,
        })
      }
    />
  );
};
